export class FenceDrawingHandler {
  constructor(mapController, map) {
    this.mapController = mapController
    this.map = map
    this.drawingManager = null
    this.currentOverlay = null
    this.existingOverlays = []
    this.drawingMode = null
    this.isDrawingModeEnabled = false
    
    // Controle de detecção drag vs click
    this.mouseDownPosition = null
    this.mouseDownTime = null
    this.isDragging = false
    this.dragThreshold = 5 // pixels
    this.clickTimeout = 200 // ms
    
    // Listeners armazenados para cleanup
    this.mapListeners = []
  }

  async init() {
    await this.initializeDrawingManager()
    this.setupEventListeners()
  }

  async initializeDrawingManager() {
    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: false,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYGON,
          google.maps.drawing.OverlayType.CIRCLE
        ]
      },
      polygonOptions: {
        fillColor: '#1F2742',
        fillOpacity: 0.3,
        strokeWeight: 2,
        strokeColor: '#1F2742',
        clickable: true,
        editable: true,
        draggable: true
      },
      circleOptions: {
        fillColor: '#1F2742',
        fillOpacity: 0.3,
        strokeWeight: 2,
        strokeColor: '#1F2742',
        clickable: true,
        editable: true,
        draggable: true
      }
    })

    // DrawingManager será adicionado/removido dinamicamente
    this.setupMapClickDetection()
  }

  setupMapClickDetection() {
    // Limpar listeners anteriores
    this.clearMapListeners()
    
    // Usar DOM events para melhor controle
    const mapDiv = this.map.getDiv()
    
    const handleMouseDown = (e) => {
      if (!this.isDrawingModeEnabled) return
      
      this.mouseDownPosition = { x: e.clientX, y: e.clientY }
      this.mouseDownTime = Date.now()
      this.isDragging = false
    }
    
    const handleMouseMove = (e) => {
      if (!this.mouseDownPosition || this.isDragging) return
      
      const deltaX = Math.abs(e.clientX - this.mouseDownPosition.x)
      const deltaY = Math.abs(e.clientY - this.mouseDownPosition.y)
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      
      if (distance > this.dragThreshold) {
        this.isDragging = true
      }
    }
    
    const handleMouseUp = (e) => {
      if (!this.mouseDownPosition || !this.isDrawingModeEnabled) return
      
      const clickDuration = Date.now() - this.mouseDownTime
      
      // Se foi um clique simples (não drag e rápido)
      if (!this.isDragging && clickDuration < this.clickTimeout) {
        this.activateDrawingForClick(e)
      }
      
      // Reset
      this.mouseDownPosition = null
      this.mouseDownTime = null
      this.isDragging = false
    }
    
    // Adicionar listeners ao DOM
    mapDiv.addEventListener('mousedown', handleMouseDown)
    mapDiv.addEventListener('mousemove', handleMouseMove)
    mapDiv.addEventListener('mouseup', handleMouseUp)
    
    // Armazenar para cleanup
    this.mapListeners = [
      { element: mapDiv, event: 'mousedown', handler: handleMouseDown },
      { element: mapDiv, event: 'mousemove', handler: handleMouseMove },
      { element: mapDiv, event: 'mouseup', handler: handleMouseUp }
    ]
  }
  
  activateDrawingForClick(domEvent) {
    // Converter coordenadas DOM para LatLng
    const mapDiv = this.map.getDiv()
    const rect = mapDiv.getBoundingClientRect()
    
    const x = domEvent.clientX - rect.left
    const y = domEvent.clientY - rect.top
    const latLng = this.pixelToLatLng(x, y)
    
    if (!latLng) return
    
    // Temporariamente remover nossos listeners para evitar interferência
    this.clearMapListeners()
    
    // Ativar o DrawingManager
    this.drawingManager.setMap(this.map)
    
    // Configurar cor atual
    const color = this.getCurrentColor()
    this.drawingManager.setOptions({
      polygonOptions: {
        ...this.drawingManager.get('polygonOptions'),
        fillColor: color,
        strokeColor: color
      },
      circleOptions: {
        ...this.drawingManager.get('circleOptions'),
        fillColor: color,
        strokeColor: color
      }
    })
    
    // Ativar modo de desenho
    if (this.drawingMode === 'polygon') {
      this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
    } else if (this.drawingMode === 'circle') {
      this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE)
    }
    
    // Simular clique após um delay para dar tempo do DrawingManager se ativar
    setTimeout(() => {
      // Criar evento sintético mais completo
      const clickEvent = {
        latLng: latLng,
        pixel: new google.maps.Point(x, y),
        stop: () => {},
        domEvent: {
          clientX: domEvent.clientX,
          clientY: domEvent.clientY,
          button: 0,
          type: 'click'
        }
      }
      
      // Disparar evento de clique
      google.maps.event.trigger(this.map, 'click', clickEvent)
      
    }, 100) // Aumentei o delay para 100ms
  }
  
  
  pixelToLatLng(x, y) {
    const bounds = this.map.getBounds()
    const projection = this.map.getProjection()
    const mapDiv = this.map.getDiv()
    
    if (!bounds || !projection) return null
    
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    
    const mapWidth = mapDiv.offsetWidth
    const mapHeight = mapDiv.offsetHeight
    
    const lng = sw.lng() + (x / mapWidth) * (ne.lng() - sw.lng())
    const lat = ne.lat() - (y / mapHeight) * (ne.lat() - sw.lat())
    
    return new google.maps.LatLng(lat, lng)
  }
  
  clearMapListeners() {
    this.mapListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler)
    })
    this.mapListeners = []
  }

  setupEventListeners() {
    if (!this.drawingManager) return

    this.drawingManager.addListener('overlaycomplete', (event) => {
      this.clearExistingOverlays()

      this.currentOverlay = event.overlay
      this.existingOverlays.push(event.overlay)

      this.saveOverlayData(event)
      this.stopDrawing()

      this.setupOverlayEditListeners(event.overlay)
      
      // Reconfigurar nossos listeners após o desenho
      setTimeout(() => {
        this.setupMapClickDetection()
      }, 100)
    })
  }

  setupOverlayEditListeners(overlay) {
    if (overlay.type === google.maps.drawing.OverlayType.POLYGON) {
      overlay.getPath().addListener('set_at', () => this.updatePolygonData(overlay))
      overlay.getPath().addListener('insert_at', () => this.updatePolygonData(overlay))
      overlay.getPath().addListener('remove_at', () => this.updatePolygonData(overlay))
      // Listeners para detectar drag do polígono
      overlay.addListener('dragend', () => this.updatePolygonData(overlay))
      overlay.addListener('dragstart', () => this.onDragStart(overlay))
      overlay.addListener('drag', () => this.onDrag(overlay))
      // Listener para click em qualquer lugar da área
      overlay.addListener('mousedown', (e) => this.onPolygonMouseDown(e, overlay))
    } else if (overlay.type === google.maps.drawing.OverlayType.CIRCLE) {
      overlay.addListener('center_changed', () => this.updateCircleData(overlay))
      overlay.addListener('radius_changed', () => this.updateCircleData(overlay))
      // Listeners para detectar drag do círculo
      overlay.addListener('dragend', () => this.updateCircleData(overlay))
      overlay.addListener('dragstart', () => this.onDragStart(overlay))
      overlay.addListener('drag', () => this.onDrag(overlay))
      // Listener para click em qualquer lugar da área
      overlay.addListener('mousedown', (e) => this.onCircleMouseDown(e, overlay))
    }
  }

  setupLoadedOverlayListeners(overlay, type) {
    if (type === 'polygon') {
      overlay.getPath().addListener('set_at', () => this.updatePolygonData(overlay))
      overlay.getPath().addListener('insert_at', () => this.updatePolygonData(overlay))
      overlay.getPath().addListener('remove_at', () => this.updatePolygonData(overlay))
      // Listeners para detectar drag do polígono carregado
      overlay.addListener('dragend', () => this.updatePolygonData(overlay))
      overlay.addListener('dragstart', () => this.onDragStart(overlay))
      overlay.addListener('drag', () => this.onDrag(overlay))
      // Listener para click em qualquer lugar da área
      overlay.addListener('mousedown', (e) => this.onPolygonMouseDown(e, overlay))
    } else if (type === 'circle') {
      overlay.addListener('center_changed', () => this.updateCircleData(overlay))
      overlay.addListener('radius_changed', () => this.updateCircleData(overlay))
      // Listeners para detectar drag do círculo carregado
      overlay.addListener('dragend', () => this.updateCircleData(overlay))
      overlay.addListener('dragstart', () => this.onDragStart(overlay))
      overlay.addListener('drag', () => this.onDrag(overlay))
      // Listener para click em qualquer lugar da área
      overlay.addListener('mousedown', (e) => this.onCircleMouseDown(e, overlay))
    }
  }

  setDrawingMode(mode) {
    this.drawingMode = mode
    this.isDrawingModeEnabled = true
    
    // Atualizar opções do DrawingManager com cor atual
    const color = this.getCurrentColor()
    if (this.drawingManager) {
      this.drawingManager.setOptions({
        polygonOptions: {
          ...this.drawingManager.get('polygonOptions'),
          fillColor: color,
          strokeColor: color
        },
        circleOptions: {
          ...this.drawingManager.get('circleOptions'),
          fillColor: color,
          strokeColor: color
        }
      })
    }
    
    // O DrawingManager NÃO é ativado no mapa ainda
    // Será ativado apenas quando detectarmos um clique simples
  }

  stopDrawing() {
    this.isDrawingModeEnabled = false
    this.drawingMode = null
    
    if (this.drawingManager) {
      this.drawingManager.setDrawingMode(null)
      this.drawingManager.setMap(null)
    }
    
    // Reset de estado
    this.mouseDownPosition = null
    this.mouseDownTime = null
    this.isDragging = false
    
    // Reconfigurar listeners se necessário
    setTimeout(() => {
      if (!this.isDrawingModeEnabled) {
        this.setupMapClickDetection()
      }
    }, 50)
  }

  saveOverlayData(event) {
    let data = null

    if (event.type === google.maps.drawing.OverlayType.POLYGON) {
      data = this.getPolygonData(event.overlay)
    } else if (event.type === google.maps.drawing.OverlayType.CIRCLE) {
      data = this.getCircleData(event.overlay)
    }

    if (data) {
      this.updateInputs(data)
    }
  }

  getPolygonData(polygon) {
    const coordinates = []
    const path = polygon.getPath()

    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i)
      coordinates.push({
        lat: point.lat(),
        lng: point.lng()
      })
    }

    return {
      type: 'polygon',
      coordinates: coordinates
    }
  }

  getCircleData(circle) {
    return {
      type: 'circle',
      center: {
        lat: circle.getCenter().lat(),
        lng: circle.getCenter().lng()
      },
      radius: circle.getRadius()
    }
  }

  updatePolygonData(polygon) {
    const data = this.getPolygonData(polygon)
    this.updateInputs(data)
  }

  updateCircleData(circle) {
    const data = this.getCircleData(circle)
    this.updateInputs(data)
  }

  updateInputs(data) {
    const jsonData = JSON.stringify(data)

    // Atualizar campo principal
    const areaInput = document.querySelector('input[name="electronic_fence[area]"]')
    if (areaInput) {
      areaInput.value = jsonData
    }

    // Atualizar campos alternativos
    const shapeInput = document.getElementById('fence_shape')
    if (shapeInput) {
      shapeInput.value = jsonData
    }

    const fenceAreaInput = document.getElementById('fence_area')
    if (fenceAreaInput) {
      fenceAreaInput.value = jsonData
    }

    // Disparar evento para notificar outros controllers
    const event = new CustomEvent('areaUpdated', { detail: data })
    document.dispatchEvent(event)
  }

  loadExistingCoords() {
    // Primeiro, tentar inputs de formulário
    const areaInput = document.querySelector('input[name="electronic_fence[area]"]') ||
      document.getElementById('fence_area') ||
      document.getElementById('fence_shape')

    let data = null

    // Se não encontrar inputs, tentar script JSON (modo show)
    if (!areaInput?.value) {
      const fenceDataScript = document.getElementById('fence-data')
      if (fenceDataScript) {
        try {
          const scriptData = JSON.parse(fenceDataScript.textContent)
          data = scriptData.coordinates
        } catch (error) {
          return
        }
      } else {
        return
      }
    } else {
      try {
        data = JSON.parse(areaInput.value)
      } catch (error) {
        return
      }
    }

    if (!data) return

    try {
      if (data.type === 'circle') {
        this.loadCircle(data)
      } else if (data.type === 'polygon' || Array.isArray(data)) {
        this.loadPolygon(data)
      }
    } catch (error) {
      // Error loading coordinates
    }
  }

  loadCircle(data) {
    const isShowMode = this.mapController.modeValue === 'show'
    
    const circle = new google.maps.Circle({
      center: data.center,
      radius: data.radius,
      fillColor: this.getCurrentColor(),
      fillOpacity: 0.3,
      strokeWeight: 2,
      strokeColor: this.getCurrentColor(),
      editable: !isShowMode,
      draggable: !isShowMode,
      clickable: !isShowMode,
      map: this.map
    })

    this.clearExistingOverlays()
    this.currentOverlay = circle
    this.existingOverlays.push(circle)
    
    if (!isShowMode) {
      this.setupLoadedOverlayListeners(circle, 'circle')
    }

    this.map.fitBounds(circle.getBounds())
  }

  loadPolygon(data) {
    const coordinates = Array.isArray(data) ? data : data.coordinates
    const isShowMode = this.mapController.modeValue === 'show'

    const polygon = new google.maps.Polygon({
      paths: coordinates,
      fillColor: this.getCurrentColor(),
      fillOpacity: 0.3,
      strokeWeight: 2,
      strokeColor: this.getCurrentColor(),
      editable: !isShowMode,
      draggable: !isShowMode,
      clickable: !isShowMode,
      map: this.map
    })

    this.clearExistingOverlays()
    this.currentOverlay = polygon
    this.existingOverlays.push(polygon)
    
    if (!isShowMode) {
      this.setupLoadedOverlayListeners(polygon, 'polygon')
    }

    const bounds = new google.maps.LatLngBounds()
    coordinates.forEach(coord => bounds.extend(coord))
    this.map.fitBounds(bounds)
  }

  clearExistingOverlays() {
    this.existingOverlays.forEach(overlay => {
      overlay.setMap(null)
    })
    this.existingOverlays = []
    this.currentOverlay = null
  }

  clearAll() {
    this.clearExistingOverlays()
    this.stopDrawing()

    // Limpar inputs
    const inputs = [
      document.querySelector('input[name="electronic_fence[area]"]'),
      document.getElementById('fence_area'),
      document.getElementById('fence_shape')
    ]

    inputs.forEach(input => {
      if (input) input.value = ''
    })
    
    // Reset de todos os estados
    this.isDrawingModeEnabled = false
    this.drawingMode = null
    this.isDragging = false
    this.mouseDownTime = null
    this.mouseDownPosition = null
  }

  updateColor(color) {
    const options = {
      fillColor: color,
      strokeColor: color
    }

    if (this.currentOverlay) {
      this.currentOverlay.setOptions(options)
    }

    if (this.drawingManager) {
      this.drawingManager.setOptions({
        polygonOptions: { ...this.drawingManager.polygonOptions, ...options },
        circleOptions: { ...this.drawingManager.circleOptions, ...options }
      })
    }
  }

  onDragStart(overlay) {
    // Optional: visual indication of dragging
  }

  onDrag(overlay) {
    // Optional: feedback during drag
  }

  onPolygonMouseDown(event, polygon) {
    // Allows polygon to be dragged by clicking anywhere in the area
    // Google Maps handles drag automatically when draggable: true
  }

  onCircleMouseDown(event, circle) {
    // Allows circle to be dragged by clicking anywhere in the area
    // Google Maps handles drag automatically when draggable: true
  }

  getCurrentColor() {
    // Primeiro, tentar input de formulário
    const colorInput = document.querySelector('input[name="electronic_fence[color]"]')
    if (colorInput?.value) {
      return colorInput.value
    }
    
    // Se não encontrar, tentar script JSON (modo show)
    const fenceDataScript = document.getElementById('fence-data')
    if (fenceDataScript) {
      try {
        const scriptData = JSON.parse(fenceDataScript.textContent)
        if (scriptData.color) {
          return scriptData.color
        }
      } catch (error) {
        // Error getting color from fence-data
      }
    }
    
    return '#1F2742'
  }

  // Cleanup method - importante para evitar memory leaks
  destroy() {
    this.clearMapListeners()
    this.clearExistingOverlays()
    
    if (this.drawingManager) {
      this.drawingManager.setMap(null)
    }
    
    // Reset de todas as propriedades
    this.isDrawingModeEnabled = false
    this.drawingMode = null
    this.isDragging = false
    this.mouseDownTime = null
    this.mouseDownPosition = null
  }
}